/**
 * Data layer for `api-key-generation-permission-per-role`.
 * Typed fetch wrappers over /api/v1/api-key-generation-permission-per-role.
 * Raw `fetch()` is confined to this file per the dash contract.
 */

export interface RolePermissionRow {
	role: string;
	can_generate: boolean;
	updated_by: string | null;
	updated_at: number;
}

export interface ListRolePermissionsResponse {
	items: RolePermissionRow[];
}

export interface UpsertRolePermissionBody {
	role: string;
	can_generate: boolean;
	updated_by?: string;
}

export interface UpsertRolePermissionResponse {
	ok: boolean;
	role: string;
	can_generate: boolean;
	updated_at: number;
}

const BASE = "/api/v1/api-key-generation-permission-per-role";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export async function list(): Promise<ListRolePermissionsResponse> {
	const res = await fetch(BASE, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ListRolePermissionsResponse>(res);
}

export async function upsert(body: UpsertRolePermissionBody): Promise<UpsertRolePermissionResponse> {
	const res = await fetch(BASE, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body),
	});
	return parseOrThrow<UpsertRolePermissionResponse>(res);
}

export const apiKeyGenerationPermissionPerRoleQueryKeys = {
	all: ["api-key-generation-permission-per-role"] as const,
	list: () => [...apiKeyGenerationPermissionPerRoleQueryKeys.all] as const,
	detail: (role: string) => [...apiKeyGenerationPermissionPerRoleQueryKeys.all, role] as const,
};
